import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  CheckCircle2,
  Edit3,
  Lock,
  RefreshCw,
  Sparkles,
  Users,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import type { WorldGraphState } from '../types'
import { BuildProgress, LastRunAlert, RunSummary, runFor } from './BuildStatus'
import { useConfirm } from './ConfirmDialog'
import { MAX_TEXT_CHARS, SETUP_LOCKED } from './ScenarioPhase'

interface Props {
  state: WorldGraphState
  /** Any step running: nothing here can change until it ends. */
  anyRunning: boolean
  canStart: boolean
  cannotStartReason: string
  onGenerate: () => Promise<boolean>
  onSave: (sidecar: string) => Promise<boolean>
  onApprove: (sidecar: string) => Promise<boolean>
  onReopen: () => Promise<boolean>
}

/**
 * The baseline qualitative context: who the teams are, what they want, how
 * the world works. Written by the model from the scenario, then reviewed,
 * edited and approved by the professor. Players never see it.
 */
export default function SidecarPhase({
  state,
  anyRunning,
  canStart,
  cannotStartReason,
  onGenerate,
  onSave,
  onApprove,
  onReopen,
}: Props) {
  const { sidecar, sidecar_approved: approved, teams } = state
  const build = runFor(state.current_build, 'sidecar')
  const running = build?.status === 'running'
  const [isEditing, setIsEditing] = useState(false)
  const [editText, setEditText] = useState(sidecar)
  const [busy, setBusy] = useState(false)
  const { confirm, dialog } = useConfirm()

  useEffect(() => {
    setEditText(sidecar)
  }, [sidecar])

  const run = async (action: () => Promise<boolean>) => {
    setBusy(true)
    const ok = await action()
    setBusy(false)
    return ok
  }

  const regenerate = async () => {
    if (
      sidecar.trim() &&
      !(await confirm({
        title: 'Write the baseline context again?',
        description: 'It replaces the current text, including your edits.',
        confirmLabel: 'Write again',
        destructive: true,
      }))
    )
      return
    run(onGenerate)
  }

  const lastRunFailed =
    build && (build.status === 'failed' || build.status === 'interrupted')

  const reopen = async () => {
    if (
      (state.stocks_approved || state.graph.nodes.length > 0) &&
      !(await confirm({
        title: 'Re-open the baseline context?',
        description: 'The stocks and links go back under review with it.',
        confirmLabel: 'Re-open',
      }))
    )
      return
    run(onReopen)
  }

  return (
    <Card className='bg-white shadow-md'>
      {dialog}
      <CardHeader>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <CardTitle className='text-lg text-dark-blue'>
              2. Baseline context
            </CardTitle>
            <CardDescription className='max-w-2xl'>
              Actor profiles, capabilities, relationships and the information
              environment, written from the scenario. It includes the team
              roster the later steps build on. Players never see it.
            </CardDescription>
          </div>
          {approved && (
            <Badge
              variant='outline'
              className='gap-1 border-green-600/30 bg-green-50 text-green-700'
            >
              <Lock className='h-3.5 w-3.5' aria-hidden />
              Approved
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        {lastRunFailed && <LastRunAlert build={build} />}

        {running && build ? (
          <BuildProgress build={build} />
        ) : !sidecar.trim() ? (
          <div className='rounded-lg border border-dashed border-gray-300 p-6'>
            <div className='flex items-center gap-2 font-medium text-dark-blue'>
              <Sparkles className='h-4 w-4' aria-hidden />
              Write the baseline context
            </div>
            <p className='mt-1 text-sm text-muted-foreground'>
              {canStart
                ? 'The model reads the scenario and writes a first draft for you to review.'
                : cannotStartReason}
            </p>
            <Button
              className='mt-4 bg-dark-blue hover:bg-dark-blue-hovered'
              disabled={!canStart || busy || anyRunning}
              onClick={() => run(onGenerate)}
            >
              <Sparkles className='h-4 w-4' aria-hidden />
              {lastRunFailed ? 'Start again' : 'Write baseline context'}
            </Button>
          </div>
        ) : (
          <>
            {teams.length > 0 && (
              <div className='flex flex-wrap items-center gap-2 text-sm'>
                <span className='inline-flex items-center gap-1 text-muted-foreground'>
                  <Users className='h-4 w-4' aria-hidden />
                  Played teams in the roster:
                </span>
                {teams.map((team) => (
                  <Badge key={team.id} variant='secondary'>
                    {team.name}
                  </Badge>
                ))}
              </div>
            )}

            <div className='rounded-lg border border-gray-200 p-6'>
              {isEditing ? (
                <Textarea
                  aria-label='Baseline context'
                  className='min-h-[420px] w-full font-mono text-sm'
                  value={editText}
                  maxLength={MAX_TEXT_CHARS}
                  onChange={(e) => setEditText(e.target.value)}
                />
              ) : (
                <div
                  className='prose prose-sm max-w-none'
                  data-testid='world-graph-sidecar'
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {sidecar}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            {build?.status === 'succeeded' && (
              <RunSummary build={build} verb='Written' />
            )}

            <div className='flex flex-wrap justify-end gap-2'>
              {approved && state.setup_locked ? (
                <p className='text-sm text-muted-foreground'>{SETUP_LOCKED}</p>
              ) : approved ? (
                <Button
                  variant='outline'
                  disabled={busy || anyRunning}
                  onClick={reopen}
                >
                  Re-open to edit
                </Button>
              ) : isEditing ? (
                <>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setEditText(sidecar)
                      setIsEditing(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={busy}
                    onClick={async () => {
                      if (await run(() => onSave(editText))) setIsEditing(false)
                    }}
                  >
                    Save changes
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant='outline'
                    disabled={busy || !canStart || anyRunning}
                    onClick={regenerate}
                  >
                    <RefreshCw className='h-4 w-4' aria-hidden />
                    Write again
                  </Button>
                  <Button variant='outline' onClick={() => setIsEditing(true)}>
                    <Edit3 className='h-4 w-4' aria-hidden />
                    Edit
                  </Button>
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={busy || anyRunning}
                    onClick={() => run(() => onApprove(sidecar))}
                  >
                    <CheckCircle2 className='h-4 w-4' aria-hidden />
                    Approve baseline context
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
